/* global fabric, File */
/**
 * In-browser PDF editor for the custom proofsheet.
 *
 * Renders a PDF with pdf.js (already loaded globally as
 * window['pdfjs-dist/build/pdf']), lets the user annotate each page on a
 * Fabric.js overlay (window.fabric), and exports a new, vector-preserving PDF
 * with pdf-lib (window.PDFLib): untouched pages are copied verbatim, pages with
 * only overlay edits keep their original vector content with a transparent PNG
 * overlay stamped on top, and pages that were rotated or raster-edited are
 * rasterized.
 *
 * Static API:
 *   PdfEditorView.open({
 *     url,            // raw PDF url (no &action=preview)
 *     fileName,       // suggested output file name
 *     title,
 *     imageSources,   // [{key, label, loader(first,pageSize)}] for the image picker
 *     onSave          // function(File pdf)
 *   });
 */
define('pdfEditorView',[
  'module',
  'backbone',
  'marionette',
  'underscore',
  'jquery',
  'imageEditorView',
  'imageSourcePickerView',
  'template!pdfEditorView'
], function (
  Module,
  Backbone,
  Marionette,
  _,
  $,
  ImageEditorView,
  ImageSourcePickerView,
  Tpl
) {
  'use strict';

  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.2.228/pdf.worker.min.js';
  var SHAPE_TOOLS = ['line', 'arrow', 'rect', 'highlight'];

  function getPdfjs() { return window['pdfjs-dist/build/pdf']; }

  var View = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'pdf-editor-view',

    ui: {
      canvas: '.pe-canvas',
      canvasArea: '.pe-canvas-area',
      canvasHost: '.pe-canvas-host',
      loader: '.pe-loader',
      error: '.pe-error',
      zoomLabel: '.pe-zoom-label',
      tools: '.pe-tool[data-tool]',
      strokeColor: '.pe-stroke-color',
      strokeWidth: '.pe-stroke-width',
      fontSize: '.pe-font-size',
      pageW: '.pe-page-w',
      pageH: '.pe-page-h',
      pageUnit: '.pe-page-unit',
      refsToggleGroup: '.pe-refs-toggle-group',
      refsToggle: '.pe-refs-toggle',
      refs: '.pe-refs',
      refsList: '.pe-refs-list',
      refLightbox: '.pe-ref-lightbox',
      refLightboxImg: '.pe-ref-lightbox-img',
      refLightboxCap: '.pe-ref-lightbox-cap'
    },

    events: {
      'click .pe-tool[data-tool]': 'onToolClick',
      'click [data-action="insert-image"]': 'onInsertImage',
      'click [data-action="rotate"]': 'onRotateActive',
      'click [data-action="extract"]': 'onExtractActive',
      'click [data-action="raster-edit"]': 'onRasterActive',
      'click [data-action="delete-object"]': 'deleteSelected',
      'click [data-action="undo"]': 'undo',
      'click [data-stamp]': 'onStampClick',
      'click [data-action="zoom-in"]': 'zoomIn',
      'click [data-action="zoom-out"]': 'zoomOut',
      'click [data-action="zoom-reset"]': 'zoomReset',
      'click [data-action="cancel"]': 'cancel',
      'click [data-action="save"]': 'save',
      'input @ui.strokeColor': 'onStrokeColorChange',
      'input @ui.strokeWidth': 'onStrokeWidthChange',
      'input @ui.fontSize': 'onFontSizeChange',
      'change @ui.pageW': 'onPageSizeChange',
      'change @ui.pageH': 'onPageSizeChange',
      'change @ui.pageUnit': 'onPageUnitChange',
      'click [data-action="toggle-refs"]': 'toggleReferences',
      'click .pe-ref': 'onRefClick',
      'click @ui.refLightbox': 'closeRefLightbox'
    },

    initialize: function (options) {
      this.options = options || {};
      this.pages = [];
      this.activeIndex = -1;
      this.activeTool = 'select';
      this.userZoom = 1;
      this.fitScale = 1;
      this.strokeColor = '#e11d48';
      this.strokeWidth = 3;
      this.fontSize = 24;
      this.pageUnit = 'mm';
      this.undoStack = [];
      this._objectUrls = [];
      this._drawing = null;
    },

    onShow: function () {
      this.renderReferences();
      // pdf.js is only needed when opening an existing PDF; blank/create mode
      // just needs Fabric (editing) and pdf-lib (export).
      var needsPdfjs = !this.options.blank;
      if (!window.fabric || !window.PDFLib || (needsPdfjs && !getPdfjs())) {
        this.showError(_.i18n('pdfEditor.libsMissing', null,
          'PDF editor libraries failed to load. Please check your connection and retry.'));
        return;
      }
      if (this.options.blank) {
        this.createBlankDoc();
      } else {
        this.showLoader(true);
        this.loadPdf();
      }
    },

    onDestroy: function () {
      if (this.fabricCanvas) {
        this.fabricCanvas.dispose();
        this.fabricCanvas = null;
      }
      _.each(this._objectUrls, function (u) {
        try { window.URL.revokeObjectURL(u); } catch (e) { /* ignore */ }
      });
    },

    /* --------------------------------------------------------------- */
    /* Loading                                                          */
    /* --------------------------------------------------------------- */

    loadPdf: function () {
      var that = this;
      var url = this.options.url;

      this.fetchBytes(url).then(function (bytes) {
        that.originalBytes = bytes;
        var pdfjsLib = getPdfjs();
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        // pdf.js detaches the buffer it's given — hand it a copy, keep the
        // original for pdf-lib on export.
        var loadingTask = pdfjsLib.getDocument({ data: bytes.slice(0) });
        loadingTask.promise.then(function (pdf) {
          that.renderAllPages(pdf);
        }, function (reason) {
          that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
          if (window.console) { console.error(reason); }
        });
      }, function () {
        that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
      });
    },

    fetchBytes: function (url) {
      var defer = $.Deferred();
      if (window.fetch) {
        window.fetch(url, { credentials: 'include' }).then(function (resp) {
          return resp.arrayBuffer();
        }).then(function (buf) {
          defer.resolve(buf);
        })['catch'](function () { defer.reject(); });
      } else {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'arraybuffer';
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) { defer.resolve(xhr.response); }
          else { defer.reject(); }
        };
        xhr.onerror = function () { defer.reject(); };
        xhr.send();
      }
      return defer.promise();
    },

    renderAllPages: function (pdf) {
      var that = this;
      // Single-page editor: only the first page is loaded and exported.
      var n = 1;
      var chain = $.Deferred().resolve().promise();

      var addPage = function (pageNum) {
        return function () {
          var d = $.Deferred();
          pdf.getPage(pageNum).then(function (page) {
            var vp1 = page.getViewport({ scale: 1 });
            var scale = Math.min(2.5, Math.max(1, 1400 / Math.max(vp1.width, vp1.height)));
            var vp = page.getViewport({ scale: scale });
            var canvas = document.createElement('canvas');
            canvas.width = Math.ceil(vp.width);
            canvas.height = Math.ceil(vp.height);
            var ctx = canvas.getContext('2d');
            page.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
              that.pages.push({
                srcIndex: pageNum - 1,
                pdfPage: page,
                scale: scale,
                ptW: vp1.width,
                ptH: vp1.height,
                baseW: canvas.width,
                baseH: canvas.height,
                baseDataUrl: canvas.toDataURL('image/png'),
                srcHasRotate: (page.rotate % 360) !== 0,
                overlayJSON: null,
                rotation: 0,
                rasterEdited: false,
                decomposed: false,
                deleted: false
              });
              d.resolve();
            }, function () { d.resolve(); });
          }, function () { d.resolve(); });
          return d.promise();
        };
      };

      for (var i = 1; i <= n; i++) {
        chain = chain.then(addPage(i));
      }

      chain.then(function () {
        that.showLoader(false);
        if (that.pages.length > 0) {
          that.activatePage(0);
        } else {
          that.showError(_.i18n('pdfEditor.loadError', null, 'Could not open the PDF.'));
        }
      });
    },

    /* --------------------------------------------------------------- */
    /* Blank / create mode                                              */
    /* --------------------------------------------------------------- */

    // Build a fresh blank page (white background) at A4 by default, or matching
    // the current page's size / the size requested via options.
    makeBlankPage: function () {
      var ptW = this.options.pageWidthPt || 595.28;
      var ptH = this.options.pageHeightPt || 841.89;
      if (this.activeIndex !== -1 && this.pages[this.activeIndex]) {
        ptW = this.pages[this.activeIndex].ptW;
        ptH = this.pages[this.activeIndex].ptH;
      }
      var scale = Math.min(2.5, Math.max(1, 1400 / Math.max(ptW, ptH)));
      var baseW = Math.round(ptW * scale);
      var baseH = Math.round(ptH * scale);
      var canvas = document.createElement('canvas');
      canvas.width = baseW;
      canvas.height = baseH;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, baseW, baseH);
      return {
        srcIndex: -1,
        blank: true,
        pdfPage: null,
        scale: scale,
        ptW: ptW,
        ptH: ptH,
        baseW: baseW,
        baseH: baseH,
        baseDataUrl: canvas.toDataURL('image/png'),
        srcHasRotate: false,
        overlayJSON: null,
        rotation: 0,
        rasterEdited: false,
        decomposed: false,
        deleted: false
      };
    },

    createBlankDoc: function () {
      this.showLoader(false);
      this.pages.push(this.makeBlankPage());
      this.activatePage(0);
    },

    /* --------------------------------------------------------------- */
    /* Thumbnails / pages                                               */
    /* --------------------------------------------------------------- */

    onExtractActive: function () {
      if (this.activeIndex !== -1) { this.decomposePage(this.activeIndex); }
    },

    onRotateActive: function () {
      if (this.activeIndex !== -1) { this.rotatePage(this.activeIndex); }
    },

    onRasterActive: function () {
      if (this.activeIndex !== -1) { this.rasterEditPage(this.activeIndex); }
    },

    // Rotate the page 90° clockwise. The background bitmap and any annotations
    // rotate together so what you see is what gets exported. Annotations are
    // transformed on an offscreen canvas (no race with the live canvas), so this
    // works whether or not the page is currently active.
    rotatePage: function (index) {
      var that = this;
      var pg = this.pages[index];
      if (this.activeIndex === index) { this.saveCurrentPageOverlay(); }

      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var oldW = swap ? pg.baseH : pg.baseW;
      var oldH = swap ? pg.baseW : pg.baseH;

      var finish = function () {
        pg.rotation = (pg.rotation + 90) % 360;
        that.activatePage(index, true);
      };

      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];
      if (!objects.length) { finish(); return; }

      var staticCanvas = new fabric.StaticCanvas(null, { width: oldW, height: oldH });
      staticCanvas.loadFromJSON({ objects: objects }, function () {
        _.each(staticCanvas.getObjects(), function (o) {
          var c = o.getCenterPoint();
          var nc = new fabric.Point(oldH - c.y, c.x);
          o.angle = (o.angle + 90) % 360;
          o.setPositionByOrigin(nc, 'center', 'center');
          o.setCoords();
        });
        var json = staticCanvas.toJSON();
        pg.overlayJSON = { objects: json.objects };
        staticCanvas.dispose();
        finish();
      });
    },

    /* --------------------------------------------------------------- */
    /* Fabric canvas                                                    */
    /* --------------------------------------------------------------- */

    activatePage: function (index, keepUndo) {
      if (this.activeIndex !== -1 && this.activeIndex !== index) {
        this.saveCurrentPageOverlay();
      }
      this.activeIndex = index;
      if (!keepUndo) { this.undoStack = []; }

      var pg = this.pages[index];
      var that = this;

      if (!this.fabricCanvas) {
        this.fabricCanvas = new fabric.Canvas(this.ui.canvas[0], {
          preserveObjectStacking: true,
          selection: true
        });
        this.bindCanvasEvents();
      }
      var canvas = this.fabricCanvas;
      canvas.clear();
      // Display the page at its (possibly rotated) on-screen orientation.
      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var w = swap ? pg.baseH : pg.baseW;
      var h = swap ? pg.baseW : pg.baseH;
      canvas.setWidth(w);
      canvas.setHeight(h);
      // Remember the page's unscaled on-screen size. applyZoom multiplies these
      // by the zoom factor and resizes the canvas element; reading the size back
      // off the (already-scaled) canvas on the next zoom would compound the
      // scaling, so the base dimensions must come from here, not getWidth().
      this._canvasW = w;
      this._canvasH = h;

      var applyBg = function () {
        that.displayBg(pg).then(function (bgUrl) {
          fabric.Image.fromURL(bgUrl, function (img) {
            img.set({
              left: 0, top: 0, originX: 'left', originY: 'top',
              scaleX: w / img.width, scaleY: h / img.height
            });
            canvas.setBackgroundImage(img, function () {
              canvas.requestRenderAll();
              that.fitAndZoom();
            });
          }, { crossOrigin: 'anonymous' });
        });
      };

      if (pg.overlayJSON && pg.overlayJSON.objects && pg.overlayJSON.objects.length) {
        canvas.loadFromJSON({ objects: pg.overlayJSON.objects }, function () {
          applyBg();
        });
      } else {
        applyBg();
      }

      this.setTool(this.activeTool, true);
      this.syncPageSizeInputs();
    },

    saveCurrentPageOverlay: function () {
      if (!this.fabricCanvas || this.activeIndex === -1) { return; }
      var pg = this.pages[this.activeIndex];
      if (!pg || pg.deleted) { return; }
      var json = this.fabricCanvas.toJSON();
      delete json.backgroundImage;
      delete json.background;
      pg.overlayJSON = json;
      // Refresh the thumbnail with the current overlay baked in is skipped for
      // perf — thumbnails show the page background only.
    },

    bindCanvasEvents: function () {
      var canvas = this.fabricCanvas;
      canvas.on('mouse:down', _.bind(this.onCanvasMouseDown, this));
      canvas.on('mouse:move', _.bind(this.onCanvasMouseMove, this));
      canvas.on('mouse:up', _.bind(this.onCanvasMouseUp, this));
      canvas.on('selection:created', _.bind(this.onSelection, this));
      canvas.on('selection:updated', _.bind(this.onSelection, this));
      canvas.on('object:modified', _.bind(function () {
        if (this._preEditJSON) {
          this.undoStack.push(this._preEditJSON);
          this._preEditJSON = null;
        }
      }, this));
    },

    fitAndZoom: function () {
      if (!this.fabricCanvas) { return; }
      var area = this.ui.canvasArea[0];
      var pad = 40;
      var maxW = (area ? area.clientWidth : 700) - pad;
      var maxH = (area ? area.clientHeight : 500) - pad;
      var w = this._canvasW || this.fabricCanvas.getWidth();
      var h = this._canvasH || this.fabricCanvas.getHeight();
      this.fitScale = Math.min(maxW / w, maxH / h, 1);
      if (!isFinite(this.fitScale) || this.fitScale <= 0) { this.fitScale = 1; }
      this.applyZoom();
    },

    applyZoom: function () {
      var z = this.fitScale * this.userZoom;
      var w = this._canvasW || this.fabricCanvas.getWidth();
      var h = this._canvasH || this.fabricCanvas.getHeight();
      this.fabricCanvas.setZoom(z);
      this.fabricCanvas.setDimensions({ width: w * z, height: h * z });
      if (this.ui.zoomLabel) { this.ui.zoomLabel.text(Math.round(z * 100) + '%'); }
    },

    zoomIn: function () { this.userZoom = Math.min(this.userZoom * 1.25, 6); this.applyZoom(); },
    zoomOut: function () { this.userZoom = Math.max(this.userZoom / 1.25, 0.2); this.applyZoom(); },
    zoomReset: function () { this.userZoom = 1; this.applyZoom(); },

    /* --------------------------------------------------------------- */
    /* Tools                                                            */
    /* --------------------------------------------------------------- */

    onToolClick: function (e) {
      this.setTool($(e.currentTarget).attr('data-tool'));
    },

    setTool: function (tool, silent) {
      this.activeTool = tool;
      this.ui.tools.removeClass('active');
      this.ui.tools.filter('[data-tool="' + tool + '"]').addClass('active');

      if (!this.fabricCanvas) { return; }
      var canvas = this.fabricCanvas;

      canvas.isDrawingMode = (tool === 'pen');
      if (tool === 'pen') {
        canvas.freeDrawingBrush.color = this.strokeColor;
        canvas.freeDrawingBrush.width = this.strokeWidth;
      }
      // Lock selection while drawing shapes so the drag creates a shape.
      canvas.selection = (tool === 'select');
      canvas.skipTargetFind = (_.contains(SHAPE_TOOLS, tool));
      canvas.defaultCursor = (_.contains(SHAPE_TOOLS, tool) || tool === 'text') ? 'crosshair' : 'default';

      if (tool === 'text' && !silent) {
        // Text is placed on the next canvas click (handled in mouse:down).
        this._pendingText = true;
      } else {
        this._pendingText = (tool === 'text');
      }
    },

    onCanvasMouseDown: function (opt) {
      var canvas = this.fabricCanvas;

      if (this.activeTool === 'select') {
        if (opt.target) { this._preEditJSON = this.serializeObjects(); }
        return;
      }

      var p = canvas.getPointer(opt.e);

      if (this.activeTool === 'text') {
        this.addText(p.x, p.y);
        this.setTool('select');
        return;
      }

      if (_.contains(SHAPE_TOOLS, this.activeTool)) {
        this.undoStack.push(this.serializeObjects());
        this._drawing = { startX: p.x, startY: p.y, obj: null };
      }
    },

    onCanvasMouseMove: function (opt) {
      if (!this._drawing) { return; }
      var canvas = this.fabricCanvas;
      var p = canvas.getPointer(opt.e);
      var d = this._drawing;

      if (d.obj) { canvas.remove(d.obj); }
      d.obj = this.makeShape(this.activeTool, d.startX, d.startY, p.x, p.y);
      if (d.obj) {
        canvas.add(d.obj);
        canvas.requestRenderAll();
      }
    },

    onCanvasMouseUp: function () {
      if (!this._drawing) { return; }
      var d = this._drawing;
      this._drawing = null;
      if (d.obj) {
        this.fabricCanvas.setActiveObject(d.obj);
        this.fabricCanvas.requestRenderAll();
      } else {
        // Nothing drawn — drop the snapshot we pushed on mouse:down.
        this.undoStack.pop();
      }
      this.setTool('select');
    },

    makeShape: function (tool, x1, y1, x2, y2) {
      var color = this.strokeColor;
      var width = this.strokeWidth;

      if (tool === 'rect' || tool === 'highlight') {
        var left = Math.min(x1, x2);
        var top = Math.min(y1, y2);
        var w = Math.abs(x2 - x1);
        var h = Math.abs(y2 - y1);
        if (w < 2 && h < 2) { return null; }
        if (tool === 'highlight') {
          return new fabric.Rect({
            left: left, top: top, width: w, height: h,
            fill: color, opacity: 0.35, stroke: null,
            globalCompositeOperation: 'multiply'
          });
        }
        return new fabric.Rect({
          left: left, top: top, width: w, height: h,
          fill: 'transparent', stroke: color, strokeWidth: width
        });
      }

      if (tool === 'line') {
        if (Math.abs(x2 - x1) < 2 && Math.abs(y2 - y1) < 2) { return null; }
        return new fabric.Line([x1, y1, x2, y2], {
          stroke: color, strokeWidth: width, strokeLineCap: 'round'
        });
      }

      if (tool === 'arrow') {
        if (Math.abs(x2 - x1) < 2 && Math.abs(y2 - y1) < 2) { return null; }
        return this.makeArrow(x1, y1, x2, y2, color, width);
      }
      return null;
    },

    makeArrow: function (x1, y1, x2, y2, color, width) {
      var line = new fabric.Line([x1, y1, x2, y2], {
        stroke: color, strokeWidth: width, strokeLineCap: 'round'
      });
      var angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
      var headSize = Math.max(10, width * 3.5);
      var head = new fabric.Triangle({
        left: x2, top: y2,
        originX: 'center', originY: 'center',
        width: headSize, height: headSize,
        fill: color,
        angle: angle + 90
      });
      return new fabric.Group([line, head]);
    },

    addText: function (x, y) {
      this.undoStack.push(this.serializeObjects());
      var t = new fabric.Textbox(_.i18n('pdfEditor.textPlaceholder', null, 'Text'), {
        left: x, top: y,
        fontSize: this.fontSize,
        fill: this.strokeColor,
        fontFamily: 'Arial',
        width: Math.max(120, this.fontSize * 6),
        editable: true
      });
      this.fabricCanvas.add(t);
      this.fabricCanvas.setActiveObject(t);
      t.enterEditing();
      t.selectAll();
      this.fabricCanvas.requestRenderAll();
    },

    onStampClick: function (e) {
      var stamp = $(e.currentTarget).attr('data-stamp');
      this.undoStack.push(this.serializeObjects());
      var canvas = this.fabricCanvas;
      var cx = canvas.getWidth() / 2;
      var cy = canvas.getHeight() / 2;
      var obj;

      if (stamp === 'date') {
        var d = new Date();
        var txt = d.getFullYear() + '-' +
          ('0' + (d.getMonth() + 1)).slice(-2) + '-' +
          ('0' + d.getDate()).slice(-2);
        obj = new fabric.Textbox(txt, {
          left: cx, top: cy, originX: 'center', originY: 'center',
          fontSize: this.fontSize, fill: this.strokeColor, fontFamily: 'Arial'
        });
      } else {
        var label = _.i18n('pdfEditor.stampApproved', null, 'APPROVED');
        var text = new fabric.Text(label, {
          fontSize: 28, fill: '#16a34a', fontFamily: 'Arial', fontWeight: 'bold',
          originX: 'center', originY: 'center', left: 0, top: 0
        });
        var box = new fabric.Rect({
          width: text.width + 28, height: text.height + 16,
          fill: 'transparent', stroke: '#16a34a', strokeWidth: 3, rx: 6, ry: 6,
          originX: 'center', originY: 'center', left: 0, top: 0
        });
        obj = new fabric.Group([box, text], {
          left: cx, top: cy, originX: 'center', originY: 'center', angle: -12
        });
      }
      canvas.add(obj);
      canvas.setActiveObject(obj);
      canvas.requestRenderAll();
    },

    onInsertImage: function () {
      ImageSourcePickerView.open({
        title: _.i18n('imageSourcePicker.title', null, 'Insert image'),
        sources: this.options.imageSources || [],
        onPick: _.bind(this.insertImage, this)
      });
    },

    insertImage: function (url) {
      if (!url || !this.fabricCanvas) { return; }
      if (/^blob:/.test(url)) { this._objectUrls.push(url); }
      var that = this;
      this.undoStack.push(this.serializeObjects());
      fabric.Image.fromURL(url, function (img) {
        if (!img) { return; }
        var canvas = that.fabricCanvas;
        var maxW = canvas.getWidth() * 0.5;
        var maxH = canvas.getHeight() * 0.5;
        var scale = Math.min(maxW / (img.width || 1), maxH / (img.height || 1), 1);
        img.set({
          left: canvas.getWidth() / 2,
          top: canvas.getHeight() / 2,
          originX: 'center', originY: 'center',
          scaleX: scale, scaleY: scale
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();
      }, { crossOrigin: 'anonymous' });
    },

    deleteSelected: function () {
      var canvas = this.fabricCanvas;
      if (!canvas) { return; }
      var active = canvas.getActiveObjects();
      if (!active || !active.length) { return; }
      this.undoStack.push(this.serializeObjects());
      _.each(active, function (o) { canvas.remove(o); });
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    },

    /* --------------------------------------------------------------- */
    /* Properties panel                                                 */
    /* --------------------------------------------------------------- */

    onSelection: function () {
      var obj = this.fabricCanvas.getActiveObject();
      if (!obj) { return; }
      if (obj.type === 'textbox' || obj.type === 'text') {
        if (obj.fill) { this.ui.strokeColor.val(this.toHex(obj.fill)); }
        if (obj.fontSize) { this.ui.fontSize.val(obj.fontSize); }
      } else if (obj.stroke) {
        this.ui.strokeColor.val(this.toHex(obj.stroke));
        if (obj.strokeWidth) { this.ui.strokeWidth.val(obj.strokeWidth); }
      }
    },

    onStrokeColorChange: function () {
      this.strokeColor = this.ui.strokeColor.val();
      if (this.fabricCanvas) {
        this.fabricCanvas.freeDrawingBrush.color = this.strokeColor;
        var obj = this.fabricCanvas.getActiveObject();
        if (obj) {
          if (obj.type === 'textbox' || obj.type === 'text') { obj.set('fill', this.strokeColor); }
          else { obj.set('stroke', this.strokeColor); }
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    onStrokeWidthChange: function () {
      this.strokeWidth = parseInt(this.ui.strokeWidth.val(), 10) || 1;
      if (this.fabricCanvas) {
        this.fabricCanvas.freeDrawingBrush.width = this.strokeWidth;
        var obj = this.fabricCanvas.getActiveObject();
        if (obj && obj.stroke && obj.type !== 'textbox' && obj.type !== 'text') {
          obj.set('strokeWidth', this.strokeWidth);
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    onFontSizeChange: function () {
      this.fontSize = parseInt(this.ui.fontSize.val(), 10) || 12;
      if (this.fabricCanvas) {
        var obj = this.fabricCanvas.getActiveObject();
        if (obj && (obj.type === 'textbox' || obj.type === 'text')) {
          obj.set('fontSize', this.fontSize);
          this.fabricCanvas.requestRenderAll();
        }
      }
    },

    /* --------------------------------------------------------------- */
    /* Page (artwork) size                                              */
    /* --------------------------------------------------------------- */

    // PDF points per millimetre (1pt = 1/72 inch, 1in = 25.4mm).
    PT_PER_MM: 72 / 25.4,

    // The active page's size expressed in the selected unit. px are the page's
    // own canvas pixels (baseW/baseH); mm is derived from the point size.
    pageSizeInUnit: function (pg) {
      if (this.pageUnit === 'px') {
        return { w: pg.baseW, h: pg.baseH };
      }
      return { w: pg.ptW / this.PT_PER_MM, h: pg.ptH / this.PT_PER_MM };
    },

    // Reflect the active page's dimensions into the toolbar inputs.
    syncPageSizeInputs: function () {
      if (!this.ui.pageW || !this.ui.pageW.length) { return; }
      var pg = this.pages[this.activeIndex];
      if (!pg) { this.ui.pageW.val(''); this.ui.pageH.val(''); return; }
      var s = this.pageSizeInUnit(pg);
      this.ui.pageW.val(Math.round(s.w * 10) / 10);
      this.ui.pageH.val(Math.round(s.h * 10) / 10);
    },

    onPageUnitChange: function () {
      this.pageUnit = (this.ui.pageUnit.val() === 'px') ? 'px' : 'mm';
      this.syncPageSizeInputs();
    },

    onPageSizeChange: function () {
      var pg = this.pages[this.activeIndex];
      if (!pg) { return; }
      var wv = parseFloat(this.ui.pageW.val());
      var hv = parseFloat(this.ui.pageH.val());
      if (!isFinite(wv) || wv <= 0 || !isFinite(hv) || hv <= 0) {
        this.syncPageSizeInputs();
        return;
      }
      // Convert the entered size to PDF points (the page's intrinsic unit).
      var newPtW, newPtH;
      if (this.pageUnit === 'px') {
        newPtW = wv / pg.scale;
        newPtH = hv / pg.scale;
      } else {
        newPtW = wv * this.PT_PER_MM;
        newPtH = hv * this.PT_PER_MM;
      }
      this.setPageSize(pg, newPtW, newPtH);
    },

    // Resize the page to newPtW × newPtH points, scaling its background bitmap
    // and every overlay object by the same factors so the whole artwork grows
    // or shrinks with the page. Resized pages export via the raster path (like
    // rotated pages) so the new size is always honoured — the original vector
    // media box cannot be rescaled cheaply.
    setPageSize: function (pg, newPtW, newPtH) {
      var idx = this.pages.indexOf(pg);
      if (idx === this.activeIndex) { this.saveCurrentPageOverlay(); }

      var newBaseW = Math.max(1, Math.round(newPtW * pg.scale));
      var newBaseH = Math.max(1, Math.round(newPtH * pg.scale));
      var fx = newBaseW / pg.baseW;
      var fy = newBaseH / pg.baseH;

      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];
      _.each(objects, function (o) {
        if (typeof o.left === 'number') { o.left *= fx; }
        if (typeof o.top === 'number') { o.top *= fy; }
        o.scaleX = (o.scaleX || 1) * fx;
        o.scaleY = (o.scaleY || 1) * fy;
      });

      pg.ptW = newPtW;
      pg.ptH = newPtH;
      pg.baseW = newBaseW;
      pg.baseH = newBaseH;
      pg.resized = true;

      this.activatePage(idx, true);
      this.syncPageSizeInputs();
    },

    /* --------------------------------------------------------------- */
    /* Undo                                                             */
    /* --------------------------------------------------------------- */

    serializeObjects: function () {
      var json = this.fabricCanvas.toJSON();
      return JSON.stringify(json.objects || []);
    },

    undo: function () {
      if (!this.fabricCanvas || !this.undoStack.length) { return; }
      var prev = this.undoStack.pop();
      var objects = JSON.parse(prev);
      var canvas = this.fabricCanvas;
      var bg = canvas.backgroundImage;
      canvas.loadFromJSON({ objects: objects }, function () {
        canvas.setBackgroundImage(bg, canvas.requestRenderAll.bind(canvas));
      });
    },

    /* --------------------------------------------------------------- */
    /* Per-page raster edit                                             */
    /* --------------------------------------------------------------- */

    rasterEditPage: function (index) {
      var pg = this.pages[index];
      var that = this;
      // Edit the natural-orientation page bitmap; rotation is re-applied on display.
      ImageEditorView.open({
        url: pg.baseDataUrl,
        fileName: 'page-' + (index + 1) + '.png',
        title: _.i18n('pdfEditor.rasterEdit', null, 'Edit image'),
        onSave: function (file) {
          var reader = new FileReader();
          reader.onload = function () {
            pg.baseDataUrl = reader.result;
            pg.rasterEdited = true;
            that.activatePage(index, true);
          };
          reader.readAsDataURL(file);
        }
      });
    },

    /* --------------------------------------------------------------- */
    /* Decompose: make existing text & images editable (best-effort)    */
    /* --------------------------------------------------------------- */

    decomposePage: function (index) {
      var pg = this.pages[index];
      if (!pg || !pg.pdfPage) { return; }
      if (pg.decomposed) {
        this.notify(_.i18n('pdfEditor.alreadyExtracted', null,
          'This page is already broken into editable objects.'));
        return;
      }
      var that = this;
      if (this.activeIndex === index) { this.saveCurrentPageOverlay(); }
      this.showLoader(true);

      var viewport = pg.pdfPage.getViewport({ scale: pg.scale });

      this.loadImageEl(pg.baseDataUrl).then(function (baseImg) {
        var sampleCanvas = document.createElement('canvas');
        sampleCanvas.width = pg.baseW;
        sampleCanvas.height = pg.baseH;
        var sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
        sampleCtx.drawImage(baseImg, 0, 0, pg.baseW, pg.baseH);

        var maskCanvas = document.createElement('canvas');
        maskCanvas.width = pg.baseW;
        maskCanvas.height = pg.baseH;
        var maskCtx = maskCanvas.getContext('2d');
        maskCtx.drawImage(baseImg, 0, 0, pg.baseW, pg.baseH);

        var measureCtx = document.createElement('canvas').getContext('2d');

        return pg.pdfPage.getTextContent().then(function (textContent) {
          var textRes = that.buildTextObjects(textContent, viewport, sampleCtx, measureCtx);
          return pg.pdfPage.getOperatorList().then(function (opList) {
            var imgRes = that.buildImageObjects(opList, viewport, pg);

            // Erase the originals from the background so moved/edited objects
            // don't leave a ghost behind them.
            var boxes = textRes.boxes.concat(imgRes.boxes);
            _.each(boxes, function (box) {
              var color = that.sampleHex(sampleCtx, box.x - 2, box.y - 2) ||
                that.sampleHex(sampleCtx, box.x + box.w + 2, box.y - 2) || '#ffffff';
              maskCtx.fillStyle = color;
              maskCtx.fillRect(Math.floor(box.x), Math.floor(box.y),
                Math.ceil(box.w), Math.ceil(box.h));
            });

            // Keep any existing annotations underneath; add extracted images
            // then text on top.
            var existing = (pg.overlayJSON && pg.overlayJSON.objects) ?
              pg.overlayJSON.objects.slice() : [];
            pg.overlayJSON = { objects: existing.concat(imgRes.objects).concat(textRes.objects) };
            pg.baseDataUrl = maskCanvas.toDataURL('image/png');
            pg.rasterEdited = true;
            pg.decomposed = true;

            that.showLoader(false);
            that.activatePage(index, true);
          });
        });
      })['catch'](function (err) {
        that.showLoader(false);
        if (window.console) { console.error('PDF decompose failed', err); }
        that.notify(_.i18n('pdfEditor.extractError', null,
          'Could not extract editable content from this page.'));
      });
    },

    buildTextObjects: function (textContent, viewport, sampleCtx, measureCtx) {
      var Util = getPdfjs().Util;
      var styles = textContent.styles || {};
      var objects = [];
      var boxes = [];

      _.each(textContent.items, function (item) {
        var str = item.str;
        if (!str || !str.replace(/\s/g, '').length) { return; }
        var tx = Util.transform(viewport.transform, item.transform);
        var fontSize = Math.hypot(tx[2], tx[3]);
        if (!fontSize || !isFinite(fontSize) || fontSize < 1) { return; }
        var angle = Math.atan2(tx[1], tx[0]) * 180 / Math.PI;
        var left = tx[4];
        var baseline = tx[5];
        var top = baseline - fontSize;
        var fontFamily = this.normalizeFont((styles[item.fontName] || {}).fontFamily);

        measureCtx.font = fontSize + 'px ' + fontFamily;
        var wdev = measureCtx.measureText(str).width || (fontSize * str.length * 0.5);
        var fill = this.sampleHex(sampleCtx, left + wdev / 2, baseline - fontSize / 3) || '#000000';

        objects.push({
          type: 'i-text',
          text: str,
          left: left,
          top: top,
          fontSize: fontSize,
          fill: fill,
          fontFamily: fontFamily,
          angle: angle,
          originX: 'left',
          originY: 'top'
        });
        boxes.push({ x: left - 1, y: top - 1, w: wdev + 2, h: fontSize * 1.3 });
      }, this);

      return { objects: objects, boxes: boxes };
    },

    buildImageObjects: function (opList, viewport, pg) {
      var pdfjsLib = getPdfjs();
      var OPS = pdfjsLib.OPS;
      var Util = pdfjsLib.Util;
      var fnArray = opList.fnArray;
      var argsArray = opList.argsArray;
      var ctm = [1, 0, 0, 1, 0, 0];
      var stack = [];
      var objects = [];
      var boxes = [];

      for (var i = 0; i < fnArray.length; i++) {
        var fn = fnArray[i];
        if (fn === OPS.save) {
          stack.push(ctm.slice());
        } else if (fn === OPS.restore) {
          ctm = stack.pop() || [1, 0, 0, 1, 0, 0];
        } else if (fn === OPS.transform) {
          ctm = Util.transform(ctm, argsArray[i]);
        } else if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) {
          this.pushImage(objects, boxes, this.resolveImageObj(pg, argsArray[i][0]), ctm, viewport);
        } else if (fn === OPS.paintInlineImage) {
          this.pushImage(objects, boxes, argsArray[i][0], ctm, viewport);
        }
      }
      return { objects: objects, boxes: boxes };
    },

    resolveImageObj: function (pg, name) {
      try {
        if (pg.pdfPage.objs.has(name)) { return pg.pdfPage.objs.get(name); }
      } catch (e1) { /* not in objs */ }
      try {
        if (pg.pdfPage.commonObjs.has(name)) { return pg.pdfPage.commonObjs.get(name); }
      } catch (e2) { /* not in commonObjs */ }
      return null;
    },

    pushImage: function (objects, boxes, imgObj, ctm, viewport) {
      if (!imgObj) { return; }
      var dataUrl = this.imgObjToDataUrl(imgObj);
      if (!dataUrl) { return; }

      var dm = getPdfjs().Util.transform(viewport.transform, ctm);
      var apply = function (x, y) {
        return { x: dm[0] * x + dm[2] * y + dm[4], y: dm[1] * x + dm[3] * y + dm[5] };
      };
      var pts = [apply(0, 0), apply(1, 0), apply(0, 1), apply(1, 1)];
      var xs = _.pluck(pts, 'x');
      var ys = _.pluck(pts, 'y');
      var left = Math.min.apply(null, xs);
      var top = Math.min.apply(null, ys);
      var w = Math.max.apply(null, xs) - left;
      var h = Math.max.apply(null, ys) - top;
      if (!isFinite(w) || !isFinite(h) || w < 2 || h < 2) { return; }

      objects.push({
        type: 'image',
        src: dataUrl,
        left: left,
        top: top,
        originX: 'left',
        originY: 'top',
        scaleX: w / imgObj.width,
        scaleY: h / imgObj.height,
        angle: 0,
        crossOrigin: 'anonymous'
      });
      boxes.push({ x: left, y: top, w: w, h: h });
    },

    imgObjToDataUrl: function (imgObj) {
      try {
        var w = imgObj.width;
        var h = imgObj.height;
        if (!w || !h) { return null; }

        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');

        var data = imgObj.data;
        if (!data) {
          // Already a drawable bitmap (canvas / image / ImageBitmap).
          ctx.drawImage(imgObj, 0, 0);
          return canvas.toDataURL('image/png');
        }

        var imageData = ctx.createImageData(w, h);
        var dst = imageData.data;
        var IK = getPdfjs().ImageKind || {};
        var n = w * h;
        var i, j;

        if (imgObj.kind === (IK.RGB_24BPP || 2) && data.length >= n * 3) {
          for (i = 0, j = 0; i < n; i++) {
            dst[j++] = data[i * 3];
            dst[j++] = data[i * 3 + 1];
            dst[j++] = data[i * 3 + 2];
            dst[j++] = 255;
          }
        } else if (imgObj.kind === (IK.GRAYSCALE_1BPP || 1)) {
          var rowBytes = (w + 7) >> 3;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              var bval = data[y * rowBytes + (x >> 3)] || 0;
              var v = ((bval >> (7 - (x & 7))) & 1) ? 255 : 0;
              var o = (y * w + x) * 4;
              dst[o] = v; dst[o + 1] = v; dst[o + 2] = v; dst[o + 3] = 255;
            }
          }
        } else if (data.length >= n * 4) {
          dst.set(data.length === n * 4 ? data : data.subarray(0, n * 4));
        } else {
          return null;
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL('image/png');
      } catch (e) {
        return null;
      }
    },

    normalizeFont: function (family) {
      if (!family) { return 'Arial'; }
      var f = family.toLowerCase();
      if (f.indexOf('mono') !== -1) { return 'monospace'; }
      if (f.indexOf('serif') !== -1 && f.indexOf('sans') === -1) { return 'serif'; }
      return 'Arial';
    },

    sampleHex: function (ctx, x, y) {
      try {
        var cx = Math.max(0, Math.min(ctx.canvas.width - 1, Math.round(x)));
        var cy = Math.max(0, Math.min(ctx.canvas.height - 1, Math.round(y)));
        var d = ctx.getImageData(cx, cy, 1, 1).data;
        if (d[3] === 0) { return null; }
        var h = function (nn) { var s = (nn | 0).toString(16); return s.length === 1 ? '0' + s : s; };
        return '#' + h(d[0]) + h(d[1]) + h(d[2]);
      } catch (e) {
        return null;
      }
    },

    loadImageEl: function (src) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = function () { reject(new Error('image load failed')); };
        img.src = src;
      });
    },

    notify: function (message) {
      if ($.notify) {
        $.notify({ message: message }, {
          type: 'info',
          placement: { from: 'top', align: 'center' },
          delay: 3000
        });
      }
    },

    /* --------------------------------------------------------------- */
    /* Export (vector-preserving)                                       */
    /* --------------------------------------------------------------- */

    save: function () {
      if (!window.PDFLib) { return; }
      this.saveCurrentPageOverlay();
      this.$el.addClass('pe-saving');
      var that = this;

      this.buildPdf().then(function (bytes) {
        var name = that.options.fileName || 'proofsheet.pdf';
        var file;
        try {
          file = new File([bytes], name, { type: 'application/pdf' });
        } catch (err) {
          file = new Blob([bytes], { type: 'application/pdf' });
          file.name = name;
        }
        if (typeof that.options.onSave === 'function') { that.options.onSave(file); }
        if (that.windowView) { that.windowView.close(); }
      })['catch'](function (err) {
        that.$el.removeClass('pe-saving');
        if (window.console) { console.error('PDF export failed', err); }
        if ($.notify) {
          $.notify({ message: _.i18n('pdfEditor.exportError', null, 'Could not export the PDF.') },
            { type: 'danger', placement: { from: 'top', align: 'center' } });
        }
      });
    },

    buildPdf: function () {
      var PDFLib = window.PDFLib;
      var that = this;
      var pages = _.filter(this.pages, function (p) { return !p.deleted; });

      // In create mode there's no source document to copy vector pages from.
      var loadSrc = this.originalBytes ?
        PDFLib.PDFDocument.load(this.originalBytes) : Promise.resolve(null);

      return loadSrc.then(function (srcDoc) {
        return PDFLib.PDFDocument.create().then(function (outDoc) {
          var chain = Promise.resolve();
          _.each(pages, function (pg) {
            chain = chain.then(function () { return that.addExportPage(outDoc, srcDoc, pg); });
          });
          return chain.then(function () { return outDoc.save(); });
        });
      });
    },

    addExportPage: function (outDoc, srcDoc, pg) {
      var that = this;
      var hasOverlay = pg.overlayJSON && pg.overlayJSON.objects && pg.overlayJSON.objects.length;
      // Blank (created) pages have no source vector content, so they are always
      // composited as a raster page.
      var needsRaster = pg.blank || pg.rotation !== 0 || pg.rasterEdited || pg.srcHasRotate || pg.resized;

      if (needsRaster) {
        return this.composePagePng(pg).then(function (res) {
          return outDoc.embedPng(res.dataUrl).then(function (png) {
            var page = outDoc.addPage([res.wPt, res.hPt]);
            page.drawImage(png, { x: 0, y: 0, width: res.wPt, height: res.hPt });
          });
        });
      }

      return outDoc.copyPages(srcDoc, [pg.srcIndex]).then(function (copied) {
        var page = copied[0];
        outDoc.addPage(page);
        if (!hasOverlay) { return null; }
        return that.renderOverlayPng(pg).then(function (dataUrl) {
          return outDoc.embedPng(dataUrl).then(function (png) {
            var size = page.getSize();
            page.drawImage(png, { x: 0, y: 0, width: size.width, height: size.height });
          });
        });
      });
    },

    exportMultiplier: function (pg) {
      var maxDisp = Math.max(pg.baseW, pg.baseH);
      return Math.min(3, Math.max(1, 2000 / maxDisp));
    },

    // The page background for display/export: the natural bitmap, pre-rotated to
    // the page's current orientation. Returns a Promise<dataUrl>.
    displayBg: function (pg) {
      if (!pg.rotation) {
        var d = $.Deferred();
        d.resolve(pg.baseDataUrl);
        return this.asPromise(d);
      }
      return this.rotateDataUrl(pg.baseDataUrl, pg.rotation);
    },

    // Transparent overlay (objects only) rendered to a PNG data URL, sized to the
    // un-rotated page so it stamps 1:1 onto the copied vector page. Only used for
    // pages that keep their original vector content (rotation 0, not rasterized).
    renderOverlayPng: function (pg) {
      var that = this;
      var defer = $.Deferred();
      var staticCanvas = new fabric.StaticCanvas(null, {
        width: pg.baseW, height: pg.baseH
      });
      staticCanvas.loadFromJSON({ objects: pg.overlayJSON.objects }, function () {
        var url = staticCanvas.toDataURL({ format: 'png', multiplier: that.exportMultiplier(pg) });
        staticCanvas.dispose();
        defer.resolve(url);
      });
      return this.asPromise(defer);
    },

    // Background (pre-rotated) + overlay flattened into one PNG in the page's final
    // orientation. Returns { dataUrl, wPt, hPt }. The display already shows this
    // orientation, so no extra rotation is applied here.
    composePagePng: function (pg) {
      var that = this;
      var defer = $.Deferred();
      var swap = (pg.rotation === 90 || pg.rotation === 270);
      var dispW = swap ? pg.baseH : pg.baseW;
      var dispH = swap ? pg.baseW : pg.baseH;
      var staticCanvas = new fabric.StaticCanvas(null, { width: dispW, height: dispH });
      var objects = (pg.overlayJSON && pg.overlayJSON.objects) ? pg.overlayJSON.objects : [];

      staticCanvas.loadFromJSON({ objects: objects }, function () {
        that.displayBg(pg).then(function (bgUrl) {
          fabric.Image.fromURL(bgUrl, function (img) {
            img.set({ scaleX: dispW / img.width, scaleY: dispH / img.height });
            staticCanvas.setBackgroundImage(img, function () {
              var flat = staticCanvas.toDataURL({ format: 'png', multiplier: that.exportMultiplier(pg) });
              staticCanvas.dispose();
              defer.resolve({
                dataUrl: flat,
                wPt: swap ? pg.ptH : pg.ptW,
                hPt: swap ? pg.ptW : pg.ptH
              });
            });
          }, { crossOrigin: 'anonymous' });
        });
      });
      return this.asPromise(defer);
    },

    rotateDataUrl: function (dataUrl, deg) {
      var defer = $.Deferred();
      if (!deg) { defer.resolve(dataUrl); return this.asPromise(defer); }
      var img = new Image();
      img.onload = function () {
        var swap = (deg === 90 || deg === 270);
        var canvas = document.createElement('canvas');
        canvas.width = swap ? img.height : img.width;
        canvas.height = swap ? img.width : img.height;
        var ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(deg * Math.PI / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        defer.resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = function () { defer.resolve(dataUrl); };
      img.src = dataUrl;
      return this.asPromise(defer);
    },

    // pdf-lib uses native Promises; bridge jQuery deferreds to real Promises so
    // they chain cleanly with pdf-lib's await points.
    asPromise: function (defer) {
      return new Promise(function (resolve, reject) {
        defer.then(resolve, reject);
      });
    },

    /* --------------------------------------------------------------- */
    /* Reference images (read-only print-area previews)                 */
    /* --------------------------------------------------------------- */

    // Render the print-area preview snapshots passed in options.referenceImages
    // into a side panel. These are reference only (never inserted/exported) so
    // the user can see where the logo sits on the product while editing.
    renderReferences: function () {
      var refs = this.options.referenceImages || [];
      if (!refs.length) {
        this.ui.refsToggleGroup.css('display', 'none');
        this.ui.refs.css('display', 'none');
        return;
      }
      var html = '';
      _.each(refs, function (ref, i) {
        var label = _.escape(ref.label || ('#' + (i + 1)));
        html += '<figure class="pe-ref" data-index="' + i + '" title="' +
          _.i18n('pdfEditor.referenceZoom', null, 'Click to enlarge') + '">' +
          '<div class="pe-ref-img"><img src="' + ref.url + '" alt="' + label + '"/></div>' +
          '<figcaption class="pe-ref-cap">' + label + '</figcaption>' +
          '</figure>';
      });
      this.ui.refsList.html(html);
      this.refsVisible = true;
      this.ui.refsToggleGroup.css('display', '');
      this.ui.refs.css('display', '');
    },

    toggleReferences: function () {
      if (!(this.options.referenceImages || []).length) { return; }
      this.refsVisible = !this.refsVisible;
      this.ui.refs.css('display', this.refsVisible ? '' : 'none');
      this.ui.refsToggle.toggleClass('active', this.refsVisible);
      if (this.fabricCanvas) { this.fitAndZoom(); }
    },

    onRefClick: function (e) {
      var idx = parseInt($(e.currentTarget).attr('data-index'), 10);
      var ref = (this.options.referenceImages || [])[idx];
      if (!ref) { return; }
      this.ui.refLightboxImg.attr('src', ref.url);
      this.ui.refLightboxCap.text(ref.label || '');
      this.ui.refLightbox.css('display', 'flex');
    },

    closeRefLightbox: function () {
      this.ui.refLightbox.css('display', 'none');
      this.ui.refLightboxImg.attr('src', '');
    },

    /* --------------------------------------------------------------- */
    /* Misc                                                             */
    /* --------------------------------------------------------------- */

    cancel: function () {
      if (this.windowView) { this.windowView.close(); }
    },

    showLoader: function (show) {
      if (this.ui.loader && this.ui.loader.length) {
        this.ui.loader.css('display', show ? 'block' : 'none');
      }
    },

    showError: function (msg) {
      this.showLoader(false);
      if (this.ui.error && this.ui.error.length) {
        this.ui.error.text(msg).css('display', 'flex');
      }
    },

    toHex: function (color) {
      if (!color) { return '#000000'; }
      if (/^#/.test(color)) { return color; }
      var m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) { return '#000000'; }
      var hex = function (n) { var s = parseInt(n, 10).toString(16); return s.length === 1 ? '0' + s : s; };
      return '#' + hex(m[1]) + hex(m[2]) + hex(m[3]);
    }
  });

  View.open = function (data) {
    require([
      'windows',
      'pdfEditorView'
    ], function (Windows, ViewCtor) {
      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'pdf-editor:' + (data && data.id ? data.id : Date.now()),
        title: (data && data.title) || _.i18n('pdfEditor.title', null, 'PDF editor'),
        modal: true,
        bodyClass: 'pdf-editor-body',
        width: '92%',
        height: '92%'
      });
    });
  };

  Module.exports = View;
});

